import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createManageCoupon,
  getManageCoupon,
  ManageCoupon,
  updateManageCoupon,
} from "../../services/manageCouponsApi";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const toDatetimeLocalValue = (value?: string | null): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toSqlDateTime = (dtLocal: string): string | null => {
  if (!dtLocal) return null;
  const [date, time] = dtLocal.split("T");
  if (!date || !time) return null;
  return `${date} ${time}:00`;
};

const parseDaysArray = (days: ManageCoupon["days"]): string[] => {
  if (!days) return [...WEEKDAYS];
  if (Array.isArray(days)) return days.map(String);
  try {
    const parsed = JSON.parse(days as string);
    return Array.isArray(parsed) ? parsed.map(String) : [...WEEKDAYS];
  } catch {
    return [...WEEKDAYS];
  }
};

const CouponFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const couponId = id ? parseInt(id, 10) : NaN;

  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [usesLimit, setUsesLimit] = useState("");
  const [discountType, setDiscountType] = useState<"percentage" | "amount">("percentage");
  const [amount, setAmount] = useState("");
  const [minimumPurchase, setMinimumPurchase] = useState("0");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"active" | "inactive" | "expire">("active");
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set(WEEKDAYS));
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleDay = (day: string) => {
    setSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const allDaysSelected = useMemo(() => selectedDays.size === WEEKDAYS.length, [selectedDays]);

  useEffect(() => {
    if (!isEdit || Number.isNaN(couponId)) return;
    const load = async () => {
      try {
        setLoading(true);
        const c = await getManageCoupon(couponId);
        setTitle(c.title || "");
        setCode(c.code || "");
        setStartLocal(toDatetimeLocalValue(c.start_date_time));
        setEndLocal(toDatetimeLocalValue(c.end_date_time));
        setUsesLimit(c.uses_limit != null && c.uses_limit > 0 ? String(c.uses_limit) : "");
        setDiscountType((c.discount_type === "amount" ? "amount" : "percentage") as "percentage" | "amount");
        setAmount(String(c.amount ?? ""));
        setMinimumPurchase(c.minimum_purchase_amount != null ? String(c.minimum_purchase_amount) : "0");
        setDescription(c.description || "");
        setStatus(((c.status || "active").toLowerCase() as "active" | "inactive" | "expire") || "active");
        setSelectedDays(new Set(parseDaysArray(c.days)));
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load coupon.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, couponId]);

  const handleSubmit = async () => {
    setError("");
    if (!title.trim()) return setError("Title is required.");
    if (!code.trim()) return setError("Code is required.");
    if (!startLocal) return setError("Start date & time is required.");
    if (selectedDays.size === 0) return setError("Select at least one day.");
    const amountNum = parseFloat(amount);
    if (Number.isNaN(amountNum) || amountNum < 0) return setError("Valid amount is required.");

    const payload: Record<string, any> = {
      title: title.trim(),
      code: code.trim(),
      start_date_time: toSqlDateTime(startLocal),
      end_date_time: endLocal ? toSqlDateTime(endLocal) : null,
      uses_limit: usesLimit.trim() === "" ? null : parseInt(usesLimit, 10),
      amount: amountNum,
      discount_type: discountType,
      minimum_purchase_amount: parseFloat(minimumPurchase || "0") || 0,
      days: Array.from(selectedDays),
      description: description.trim() || null,
      status,
    };

    try {
      setSaving(true);
      if (isEdit && !Number.isNaN(couponId)) await updateManageCoupon(couponId, payload);
      else await createManageCoupon(payload);
      navigate("/manage/coupons");
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === "object") setError(Object.values(errs).flat().join(" ") || msg || "Validation failed.");
      else setError(msg || "Could not save coupon.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-600">Loading coupon...</div>;

  return (
    <div className="space-y-5 p-1 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">{isEdit ? "Edit Coupon" : "Add Coupon"}</h1>
          <p className="text-sm text-gray-500">Manage coupon for your company.</p>
        </div>
        <button type="button" onClick={() => navigate("/manage/coupons")} className="text-sm font-semibold text-[#402B69] hover:underline">Back to list</button>
      </div>
      {error ? <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</div> : null}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Coupon title" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="w-full border rounded-lg px-3 py-2 text-sm uppercase" />
            <input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input type="number" min={0} value={usesLimit} onChange={(e) => setUsesLimit(e.target.value)} placeholder="Uses limit" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <select value={discountType} onChange={(e) => setDiscountType(e.target.value as "percentage" | "amount")} className="w-full border rounded-lg px-3 py-2 text-sm"><option value="percentage">Percentage</option><option value="amount">Fixed amount</option></select>
            <input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount / percent" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input type="number" min={0} step="0.01" value={minimumPurchase} onChange={(e) => setMinimumPurchase(e.target.value)} placeholder="Minimum purchase amount" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="w-full border rounded-lg px-3 py-2 text-sm"><option value="active">Active</option><option value="inactive">Inactive</option><option value="expire">Expired</option></select>
          </div>
          <div className="flex flex-wrap gap-3">
            {WEEKDAYS.map((day) => (
              <label key={day} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selectedDays.has(day)} onChange={() => toggleDay(day)} className="rounded border-gray-300" />
                {day}
              </label>
            ))}
          </div>
          {allDaysSelected ? <p className="text-xs text-gray-500">All days selected.</p> : null}
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Description" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2 pt-2">
            <button type="button" disabled={saving} onClick={handleSubmit} className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            <button type="button" onClick={() => navigate("/manage/coupons")} className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-5 py-2 rounded-lg text-sm font-semibold">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponFormPage;

